import { useEffect, useMemo } from 'react';
import { OnboardingProTask } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useOnboardingShallowStore } from '@frontend/product-onboarding';
import { useNavigate } from '@frontend/weave-router';
import { CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS } from '../constant';
import { useProOnboardingActionShallowStore } from './use-pro-onboarding-action-store';

type SentimentDrilldownTarget = {
  id: CallIntelTypes.SentimentEnum;
  type: 'sentiment';
};

type Sentiments = Partial<Record<CallIntelTypes.SentimentEnum | string, number>>;

type UseProOnboardingSentimentDrilldownArgs = {
  enabled?: boolean;
  isLoading?: boolean;
  sentiments?: Sentiments | null;
  sentimentChartTrackingId: string;
  setSubView: (subView: SentimentDrilldownTarget) => void;
};

/**
 * Ordered fallback list for PRO onboarding Task 2.
 *
 * We prefer Unhappy/Negative first because it is the most useful drilldown.
 * If negative is not available, we use Happy/Positive.
 * If positive is also missing, Neutral is expected to be present.
 */
const PRO_ONBOARDING_SENTIMENT_FALLBACKS: CallIntelTypes.SentimentEnum[] = [
  CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE,
  CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE,
  CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL,
];

const SENTIMENT_DRILLDOWN_HOTSPOT_KEY = CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.OVERVIEW_SENTIMENT_UNHAPPY_CHART_SEGMENT;

/**
 * Handles PRO onboarding Task 2 drilldown resolution.
 *
 * This hook owns:
 * - which sentiment segment should be used as the actual DOM target
 * - checklist-arrow eligibility
 * - checklist-arrow navigation behavior
 */
export function useProOnboardingSentimentDrilldown({
  enabled = true,
  isLoading,
  sentiments,
  sentimentChartTrackingId,
  setSubView,
}: UseProOnboardingSentimentDrilldownArgs) {
  const navigate = useNavigate();

  const { completeHotspot: markHotspotComplete, completedHotspots } = useOnboardingShallowStore(
    'completeHotspot',
    'completedHotspots'
  );

  const { pendingTask, clearPendingTask, setEligibility } = useProOnboardingActionShallowStore(
    'pendingTask',
    'clearPendingTask',
    'setEligibility'
  );

  const isTaskPending = pendingTask === OnboardingProTask.PRO_TASK_OVERVIEW_CHART_DRILLDOWN;

  const targetSentiment = useMemo(() => {
    if (!sentiments) return undefined;

    return PRO_ONBOARDING_SENTIMENT_FALLBACKS.find((sentiment) => {
      return (sentiments[sentiment] ?? 0) > 0;
    });
  }, [sentiments]);

  const sentimentDrilldownTargetSelector = useMemo(() => {
    if (!enabled) return undefined;
    if (!targetSentiment) return undefined;

    return `[data-trackingid="${sentimentChartTrackingId}:${targetSentiment}"]`;
  }, [enabled, targetSentiment, sentimentChartTrackingId]);

  /**
   * Resolve checklist-arrow eligibility once chart data is available.
   */
  useEffect(() => {
    if (!enabled) return;
    if (isLoading) return;
    if (!sentiments) return;

    setEligibility({
      hasSentimentDrilldownTarget: !!targetSentiment,
    });
  }, [enabled, isLoading, sentiments, targetSentiment, setEligibility]);

  /**
   * Execute pending Task 2 intent.
   */
  useEffect(() => {
    if (!enabled) return;
    if (!isTaskPending) return;
    if (isLoading) return;

    if (!targetSentiment) {
      clearPendingTask();
      return;
    }

    clearPendingTask();

    setSubView({
      id: targetSentiment,
      type: 'sentiment',
    });

    if (!completedHotspots.has(SENTIMENT_DRILLDOWN_HOTSPOT_KEY)) {
      markHotspotComplete(SENTIMENT_DRILLDOWN_HOTSPOT_KEY);
    }

    navigate({
      to: '/analytics/call-intelligence/$id',
      params: {
        id: targetSentiment.toLowerCase(),
      },
      search: {
        type: 'sentiment',
      },
    });
  }, [
    enabled,
    isTaskPending,
    isLoading,
    targetSentiment,
    clearPendingTask,
    setSubView,
    completedHotspots,
    markHotspotComplete,
    navigate,
  ]);

  return {
    targetSentiment,
    sentimentDrilldownTargetSelector,
  };
}
